// MIT License
//
// Copyright (c) 2019-PRESENT Anthony Fu<https://github.com/antfu>
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

import { useElementSize, useEventListener } from '@vueuse/core'
import type { Ref } from 'vue'
import { computed, ref, watch } from 'vue'

/**
 * Modified and trimmed down implementation of `@vueuse/core/useVirtualList`
 *
 * This implementation exposes the internal state and supports only vertical scroll compared to the original.
 * It also uses throttling to improve performance
 *
 * For testing, you should probably provide a mock implementation which just returns the items passed in:
 *
 * ```typescript
 * vi.mock('@/sharedComposables/useVerticalVirtualList', () => ({
 *   useVerticalVirtualList: (items: Ref<unknown[]>) => ({
 *     containerProps: {},
 *     wrapperProps: {},
 *     scrollTo: vi.fn(),
 *     list: computed(() => items.value.map((data, index) => ({ data, index }))),
 *   }),
 * }))
 * ```
 */
export function useVerticalVirtualList<T>(
  list: Ref<T[]>,
  {
    itemHeight,
    overscan = 5,
    containerRef,
  }: {
    /**
     * item height, accept a pixel value or a function that returns the height
     *
     * @default 0
     */
    itemHeight: number | ((index: number) => number)
    /**
     * the extra buffer items outside of the view area
     *
     * @default 5
     */
    overscan?: number
    /**
     * The ref to the container element.
     */
    containerRef: Ref<HTMLElement | null>
  },
) {
  const size = useElementSize(containerRef)

  const currentList: Ref<{ data: T; index: number }[]> = ref([])

  const state: Ref<{ start: number; end: number }> = ref({
    start: 0,
    end: Math.min(list.value.length || 100, 100),
  })

  watch(
    containerRef,
    (newRef) => {
      if (!newRef) {
        return
      }

      newRef.style.overflow = 'auto'
    },
    {
      immediate: true,
    },
  )

  const getViewCapacity = (containerSize: number) => {
    if (typeof itemHeight === 'number') return Math.ceil(containerSize / itemHeight)

    const { start = 0 } = state.value
    let sum = 0
    let capacity = 0
    for (let i = start; i < list.value.length; i++) {
      sum += itemHeight(i)
      capacity = i
      if (sum > containerSize) break
    }
    return capacity - start
  }

  const getOffset = (scrollTop: number) => {
    if (typeof itemHeight === 'number') return Math.floor(scrollTop / itemHeight) + 1

    let sum = 0
    let offset = 0

    for (let i = 0; i < list.value.length; i++) {
      sum += itemHeight(i)
      if (sum >= scrollTop) {
        offset = i
        break
      }
    }
    return offset + 1
  }

  const calculateRange = () => {
    const element = containerRef.value
    if (element) {
      const offset = getOffset(element.scrollTop)
      const viewCapacity = getViewCapacity(element.clientHeight)

      const from = offset - overscan
      const to = offset + viewCapacity + overscan
      state.value = {
        start: from < 0 ? 0 : from,
        end: to > list.value.length ? list.value.length : to,
      }
      currentList.value = list.value
        .slice(state.value.start, state.value.end)
        .map((ele, index) => ({
          data: ele,
          index: index + state.value.start,
        }))
    }
  }

  const getDistanceTop = (index: number) => {
    if (typeof itemHeight === 'number') {
      return index * itemHeight
    }

    return list.value.slice(0, index).reduce((sum, _, i) => sum + itemHeight(i), 0)
  }

  const offsetTop = computed(() => getDistanceTop(state.value.start))

  const totalHeight = computed(() => {
    // Add one to account for the header row
    if (typeof itemHeight === 'number') return (list.value.length + 1) * itemHeight

    return list.value.reduce((sum, _, index) => sum + itemHeight(index), 0)
  })

  watch([size.height, list], () => {
    calculateRange()
  })

  const scrollTo = (index: number) => {
    if (containerRef.value) {
      containerRef.value.scrollTop = getDistanceTop(index)
      calculateRange()
    }
  }

  useEventListener(containerRef, 'scroll', () => {
    calculateRange()
  })

  const wrapperProps = computed(() => {
    return {
      style: {
        width: '100%',
        height: `${totalHeight.value - offsetTop.value}px`,
        marginTop: `${offsetTop.value}px`,
      },
    }
  })

  return {
    list: currentList,
    scrollTo,
    wrapperProps,
    state,
  }
}
